<template>
    <Card pageTitle="Loyalty Management">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_LOYALTY">
            <div class="loyalty-management">
                <header class="page-head">
                    <div>
                        <p class="eyebrow">Customer engagement</p>
                        <h2>Loyalty Management</h2>
                        <p class="intro">Manage customer points and the operational parts of the owned loyalty program.</p>
                    </div>
                    <div :class="['program-state', programStateClass]">
                        <span class="state-dot"></span>
                        <div>
                            <span class="state-label">Program status</span>
                            <strong>{{ programStateLabel }}</strong>
                        </div>
                    </div>
                </header>

                <section v-if="settings" class="configuration-strip" aria-label="Current loyalty configuration">
                    <div>
                        <span>Points per visit</span>
                        <strong>{{ settings.pointsPerVisit }}</strong>
                    </div>
                    <div>
                        <span>Earn cooldown</span>
                        <strong>{{ settings.cooldownHours }} hours</strong>
                    </div>
                    <div>
                        <span>Points expiry</span>
                        <strong>{{ settings.pointsExpiryDays ? `${settings.pointsExpiryDays} days` : "No expiry" }}</strong>
                    </div>
                    <router-link :to="ROUTE_PATHS.LOYALTY">Edit program rules <span aria-hidden="true">→</span></router-link>
                </section>

                <div v-if="settingsError" class="cutover-notice error" role="alert">
                    <span class="material-icons" aria-hidden="true">error_outline</span>
                    <div>
                        <strong>Current loyalty configuration is unavailable.</strong>
                        <p>{{ settingsError }}</p>
                    </div>
                </div>
                <div v-else-if="settings && !settings.enabled" class="cutover-notice" role="status">
                    <span class="material-icons" aria-hidden="true">info</span>
                    <div>
                        <strong>The owned loyalty program is not active.</strong>
                        <p>You can review configuration and customer records, but POS earning remains off until the program is enabled.</p>
                    </div>
                </div>

                <section v-if="canSearchCustomers" class="customer-workbench">
                    <div class="section-heading">
                        <div>
                            <p class="eyebrow">Customer points</p>
                            <h3>Find and manage a customer</h3>
                        </div>
                        <router-link :to="ROUTE_PATHS.CUSTOMERS" class="secondary-link">Open all customers</router-link>
                    </div>

                    <form class="customer-search" @submit.prevent="searchCustomers">
                        <label for="loyalty-customer-search">Name, email, or phone</label>
                        <div class="search-row">
                            <input
                                id="loyalty-customer-search"
                                v-model.trim="customerSearch"
                                type="search"
                                autocomplete="off"
                                placeholder="Search customers"
                            />
                            <button type="submit" :disabled="searchingCustomers">
                                {{ searchingCustomers ? "Searching…" : "Search" }}
                            </button>
                        </div>
                    </form>

                    <p v-if="customerSearchError" class="inline-error">{{ customerSearchError }}</p>
                    <div v-else-if="searchingCustomers" class="search-state">Searching customer records…</div>
                    <div v-else-if="hasSearched && !customers.length" class="search-state">
                        No matching customers found.
                    </div>
                    <div v-else-if="customers.length" class="customer-results">
                        <div class="result-head" aria-hidden="true">
                            <span>Customer</span>
                            <span>Contact</span>
                            <span>Current points</span>
                            <span></span>
                        </div>
                        <div v-for="customer in customers" :key="customer.id" class="customer-row">
                            <div>
                                <strong>{{ customerDisplayName(customer) }}</strong>
                                <span class="customer-id">{{ customer.id }}</span>
                            </div>
                            <span>{{ customer.email_id || customer.mobile_no || "No contact on file" }}</span>
                            <strong class="points-value">
                                {{ customer.points_balance === null ? "Unavailable" : `${customer.points_balance} pts` }}
                            </strong>
                            <router-link :to="customerLoyaltyPath(customer.id)" class="manage-link">Manage points</router-link>
                        </div>
                    </div>
                </section>
                <section v-else class="customer-workbench">
                    <div class="section-heading">
                        <div>
                            <p class="eyebrow">Customer points</p>
                            <h3>Customer access required</h3>
                        </div>
                    </div>
                    <p class="access-note">
                        Your role can review loyalty operations, but customer search requires customer-view access.
                    </p>
                </section>

                <section class="operations">
                    <div class="section-heading">
                        <div>
                            <p class="eyebrow">Program operations</p>
                            <h3>Configure and monitor</h3>
                        </div>
                    </div>
                    <div class="operation-list">
                        <router-link
                            v-for="operation in visibleOperations"
                            :key="operation.path"
                            :to="operation.path"
                            class="operation-row"
                        >
                            <span class="material-icons" aria-hidden="true">{{ operation.icon }}</span>
                            <span>
                                <strong>{{ operation.title }}</strong>
                                <small>{{ operation.description }}</small>
                            </span>
                            <span class="material-icons operation-arrow" aria-hidden="true">arrow_forward</span>
                        </router-link>
                    </div>
                </section>

                <section class="replacement-status">
                    <div class="section-heading">
                        <div>
                            <p class="eyebrow">Springbig replacement</p>
                            <h3>Cutover readiness</h3>
                        </div>
                        <span class="planning-label">Planning status</span>
                    </div>
                    <div class="readiness-list">
                        <div>
                            <span class="readiness-mark complete material-icons" aria-hidden="true">check</span>
                            <p><strong>Owned points engine</strong><small>Earning, redemption, adjustments, audit, and refund safeguards are implemented.</small></p>
                        </div>
                        <div>
                            <span class="readiness-mark pending material-icons" aria-hidden="true">schedule</span>
                            <p><strong>Customer balance migration</strong><small>Springbig balances and membership records still require an approved import and reconciliation plan.</small></p>
                        </div>
                        <div>
                            <span class="readiness-mark pending material-icons" aria-hidden="true">schedule</span>
                            <p><strong>POS rollout</strong><small>Production deployment, tablet release, and operator confirmation remain separate cutover gates.</small></p>
                        </div>
                        <div>
                            <span class="readiness-mark pending material-icons" aria-hidden="true">schedule</span>
                            <p><strong>Email and SMS/MMS campaigns</strong><small>Messaging delivery providers must be completed before Springbig marketing can be retired.</small></p>
                        </div>
                    </div>
                </section>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import Card from "../../components/UI/Card.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { mapState } from "vuex"
import { http } from "../../utils/http"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { ROUTE_PATHS, customerLoyaltyPath } from "../../router/route-paths"
import LoyaltyFetchApi from "../../utils/fetch/loyalty-fetch-api"
import { errMsg } from "../../utils/common"

export default {
    components: { Card, AllowPermission },
    data() {
        return {
            PERMISSION_CODES,
            ROUTE_PATHS,
            settings: null,
            settingsLoading: true,
            settingsError: "",
            customerSearch: "",
            customers: [],
            hasSearched: false,
            searchingCustomers: false,
            customerSearchError: "",
            operations: [
                {
                    title: "Program rules",
                    description: "Earning, cooldown, expiry, and refund clawback.",
                    path: ROUTE_PATHS.LOYALTY,
                    icon: "tune",
                },
                {
                    title: "Rewards",
                    description: "Eligible merchandise, point costs, and approvals.",
                    path: ROUTE_PATHS.LOYALTY_REWARDS,
                    icon: "redeem",
                    permission: PERMISSION_CODES.CMS_VIEW_LOYALTY_REWARD,
                },
                {
                    title: "Tiers",
                    description: "Visit thresholds and customer status levels.",
                    path: ROUTE_PATHS.LOYALTY_TIERS,
                    icon: "workspace_premium",
                },
                {
                    title: "Bonus campaigns",
                    description: "Approved bonus-point campaigns and grants.",
                    path: ROUTE_PATHS.LOYALTY_CAMPAIGNS,
                    icon: "campaign",
                },
                {
                    title: "Program health",
                    description: "Balance integrity and daily operational checks.",
                    path: ROUTE_PATHS.LOYALTY_HEALTH,
                    icon: "monitor_heart",
                },
                {
                    title: "Reconciliation",
                    description: "Investigate and resolve loyalty exceptions.",
                    path: ROUTE_PATHS.LOYALTY_RECONCILIATION,
                    icon: "rule",
                },
            ],
        }
    },
    async mounted() {
        await this.loadSettings()
    },
    computed: {
        ...mapState("auth", ["user"]),
        canSearchCustomers() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_VIEW_CUSTOMER,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        visibleOperations() {
            return this.operations.filter((operation) => !operation.permission || checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: operation.permission,
                userPermissionIds: this.user?.permissionIds || [],
            }))
        },
        programStateLabel() {
            if (this.settingsLoading) return "Loading"
            if (this.settingsError || !this.settings) return "Unavailable"
            return this.settings.enabled ? "Enabled" : "Disabled"
        },
        programStateClass() {
            if (this.settingsLoading || this.settingsError || !this.settings) return "unknown"
            return this.settings.enabled ? "enabled" : "disabled"
        },
    },
    methods: {
        customerLoyaltyPath,
        async loadSettings() {
            this.settingsLoading = true
            this.settingsError = ""
            try {
                const settings = await LoyaltyFetchApi.fetchSettings()
                this.settings = settings || null
            } catch (err) {
                this.settings = null
                this.settingsError = errMsg(err, "Could not load loyalty settings")
                this.$toast.error(this.settingsError)
            } finally {
                this.settingsLoading = false
            }
        },
        customerDisplayName(customer) {
            return customer.customer_name ||
                [customer.first_name, customer.last_name].filter(Boolean).join(" ") ||
                "Unnamed customer"
        },
        async searchCustomers() {
            if (!this.canSearchCustomers) return
            if (this.searchingCustomers) return
            const search = this.customerSearch.trim()
            this.hasSearched = true
            this.customerSearchError = ""
            this.customers = []
            if (search.length < 2) {
                this.customerSearchError = "Enter at least two characters to search."
                return
            }

            this.searchingCustomers = true
            try {
                const params = new URLSearchParams({
                    search,
                    start: "0",
                    limit: "10",
                    sort: "last_name",
                    direction: "asc",
                })
                const response = await http.get(`/customers?${params.toString()}`)
                const customers = response.data?.result || []
                this.customers = await Promise.all(customers.map(async (customer) => {
                    try {
                        const account = await LoyaltyFetchApi.fetchCustomerLoyalty(customer.id)
                        return { ...customer, points_balance: Number(account?.points_balance || 0) }
                    } catch (err) {
                        return { ...customer, points_balance: null }
                    }
                }))
            } catch (err) {
                this.customerSearchError = errMsg(err, "Could not search customers")
            } finally {
                this.searchingCustomers = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.loyalty-management {
    max-width: 1180px;
    color: #172322;
}

.page-head,
.section-heading,
.configuration-strip,
.customer-row,
.result-head,
.operation-row,
.readiness-list > div {
    display: flex;
    align-items: center;
}

.page-head {
    justify-content: space-between;
    gap: 28px;
    padding-bottom: 24px;
    border-bottom: 1px solid #dfe8e5;
}

.eyebrow {
    margin: 0 0 5px;
    color: #4f716c;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

h2,
h3,
p {
    margin-top: 0;
}

h2 {
    margin-bottom: 6px;
    color: #0b302c;
    font-size: 28px;
    font-weight: 800;
}

h3 {
    margin-bottom: 0;
    color: #102f2c;
    font-size: 19px;
    font-weight: 750;
}

.intro {
    margin-bottom: 0;
    color: #63736f;
}

.program-state {
    display: flex;
    align-items: center;
    gap: 11px;
    min-width: 170px;
    padding: 12px 0 12px 18px;
    border-left: 1px solid #dfe8e5;
}

.state-dot {
    width: 11px;
    height: 11px;
    border-radius: 50%;
}

.program-state.enabled .state-dot {
    background: #27804a;
    box-shadow: 0 0 0 4px #e4f3e9;
}

.program-state.disabled .state-dot {
    background: #a76b19;
    box-shadow: 0 0 0 4px #fbefdc;
}

.program-state.unknown .state-dot {
    background: #788784;
    box-shadow: 0 0 0 4px #edf1f0;
}

.state-label,
.configuration-strip span,
.customer-id,
.operation-row small,
.readiness-list small {
    display: block;
    color: #6a7976;
    font-size: 12px;
}

.program-state strong {
    display: block;
    margin-top: 2px;
    color: #173a35;
}

.configuration-strip {
    gap: 0;
    padding: 18px 0;
    border-bottom: 1px solid #dfe8e5;
}

.configuration-strip > div {
    min-width: 160px;
    padding-right: 28px;
}

.configuration-strip strong {
    display: block;
    margin-top: 4px;
    color: #153c37;
    font-size: 15px;
}

.configuration-strip > a {
    margin-left: auto;
    color: #0d6257;
    font-size: 13px;
    font-weight: 750;
    text-decoration: none;
}

.cutover-notice {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin: 20px 0 0;
    padding: 14px 16px;
    border-left: 3px solid #b57a28;
    background: #fffaf1;
    color: #4f3a1f;
}

.cutover-notice p {
    margin: 3px 0 0;
    color: #705b3e;
    font-size: 13px;
}

.cutover-notice.error {
    border-left-color: #a33b32;
    background: #fff6f5;
    color: #782d27;
}

.cutover-notice.error p {
    color: #8b4b45;
}

.customer-workbench,
.operations,
.replacement-status {
    padding: 30px 0;
    border-bottom: 1px solid #dfe8e5;
}

.section-heading {
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 18px;
}

.secondary-link,
.manage-link {
    color: #0d6257;
    font-size: 13px;
    font-weight: 750;
    text-decoration: none;
}

.customer-search {
    max-width: 760px;
}

.customer-search label {
    display: block;
    margin-bottom: 7px;
    color: #344844;
    font-size: 13px;
    font-weight: 650;
}

.search-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
}

.search-row input {
    min-width: 0;
    height: 42px;
    padding: 0 13px;
    border: 1px solid #bfcfcb;
    border-radius: 6px;
    background: #fff;
    color: #172322;
    outline: none;
}

.search-row input:focus {
    border-color: #0d6257;
    box-shadow: 0 0 0 3px rgba(13, 98, 87, 0.12);
}

.search-row button {
    min-width: 112px;
    border: 0;
    border-radius: 6px;
    background: #0b4a43;
    color: #fff;
    font-weight: 750;
    cursor: pointer;
    transition: background-color 150ms ease, transform 150ms ease;
}

.search-row button:hover:not(:disabled) {
    background: #073b36;
    transform: translateY(-1px);
}

.search-row button:disabled {
    cursor: wait;
    opacity: 0.65;
}

.search-state,
.inline-error {
    margin: 18px 0 0;
    color: #697976;
    font-size: 13px;
}

.inline-error {
    color: #a33b32;
}

.access-note {
    max-width: 650px;
    margin: 0;
    color: #697976;
    font-size: 13px;
}

.customer-results {
    margin-top: 22px;
    border-top: 1px solid #dfe8e5;
}

.result-head,
.customer-row {
    display: grid;
    grid-template-columns: minmax(220px, 1.4fr) minmax(190px, 1fr) 120px 120px;
    gap: 18px;
    padding: 12px 8px;
}

.result-head {
    color: #6b7b78;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.customer-row {
    min-height: 68px;
    border-top: 1px solid #e7eeec;
    transition: background-color 150ms ease;
}

.customer-row:hover {
    background: #f7faf9;
}

.customer-row > span {
    color: #52635f;
    font-size: 13px;
}

.customer-id {
    max-width: 260px;
    margin-top: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.points-value {
    color: #143f39;
}

.operation-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-top: 1px solid #dfe8e5;
}

.operation-row {
    gap: 13px;
    min-height: 78px;
    padding: 14px 12px;
    border-bottom: 1px solid #dfe8e5;
    color: #17332f;
    text-decoration: none;
    transition: background-color 150ms ease, padding-left 150ms ease;
}

.operation-row:nth-child(odd) {
    border-right: 1px solid #dfe8e5;
}

.operation-row:hover {
    padding-left: 16px;
    background: #f5f9f8;
}

.operation-row > .material-icons:first-child {
    color: #3e726a;
}

.operation-row strong,
.operation-row small {
    display: block;
}

.operation-row small {
    margin-top: 3px;
    line-height: 1.35;
}

.operation-arrow {
    margin-left: auto;
    color: #88a29d;
    font-size: 18px;
}

.replacement-status {
    border-bottom: 0;
}

.planning-label {
    padding: 5px 9px;
    border-radius: 999px;
    background: #edf3f1;
    color: #54706b;
    font-size: 11px;
    font-weight: 750;
}

.readiness-list {
    border-top: 1px solid #dfe8e5;
}

.readiness-list > div {
    gap: 13px;
    padding: 14px 8px;
    border-bottom: 1px solid #e7eeec;
}

.readiness-list p {
    margin: 0;
}

.readiness-list strong,
.readiness-list small {
    display: block;
}

.readiness-list small {
    margin-top: 3px;
    line-height: 1.4;
}

.readiness-mark {
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    flex: 0 0 26px;
    border-radius: 50%;
    font-size: 16px;
}

.readiness-mark.complete {
    background: #e2f2e7;
    color: #237344;
}

.readiness-mark.pending {
    background: #f7eddd;
    color: #9a641a;
}

@media (max-width: 820px) {
    .page-head,
    .configuration-strip {
        align-items: flex-start;
        flex-direction: column;
    }

    .program-state {
        padding-left: 0;
        border-left: 0;
    }

    .configuration-strip {
        gap: 14px;
    }

    .configuration-strip > a {
        margin-left: 0;
    }

    .result-head {
        display: none;
    }

    .customer-row {
        grid-template-columns: 1fr auto;
    }

    .customer-row > span,
    .points-value {
        grid-column: 1;
    }

    .manage-link {
        grid-column: 2;
        grid-row: 1 / span 3;
        align-self: center;
    }

    .operation-list {
        grid-template-columns: 1fr;
    }

    .operation-row:nth-child(odd) {
        border-right: 0;
    }
}

@media (max-width: 560px) {
    .search-row {
        grid-template-columns: 1fr;
    }

    .search-row button {
        height: 42px;
    }

    .customer-row {
        grid-template-columns: 1fr;
    }

    .manage-link {
        grid-column: 1;
        grid-row: auto;
    }
}
</style>
